import "../../assets/css/order-confirmation.css";
import "../../assets/css/tour/tour-order-confirmation.css";
import { useState, useEffect } from "react";
import TourOrderDetails from "./TourOrderDetails";
import TourOrderPhotoUpload from "./TourOrderPhotoUpload";
import TourOrderConfirmationText from "./TourOrderConfirmationText";
import appSettings from "../../settings/appSettings";
import TourOrderConfirmCheck from "./TourOrderConfirmCheck";
import TourOrderConfirmComplete from "./TourOrderConfirmComplete";
import {
  hasPostOrderFlowCompletedId,
  addPostOrderFlowCompletedId,
  getQueryParam,
  wvusLogger
} from "../../helpers";
import { requestEletter } from "../../services/eletterService";
import { requestSakEletter } from "../../services/sakEletterService";
import TourOrderQuestionnaire from "./TourOrderQuestionnaire";
import { OrderData } from "../../types";
import { getFallbackChildData } from "../../helpers";
import SAKConfirmationHeader from "./SAK/SAKConfirmationHeader";
import SAKPreviewContainer from "./SAK/SAKPreviewContainer";
import {
  retrieveSakPhotoData,
  retrieveSakQuestionnaireData,
  retrieveSakSponsorData,
  storeSakPhotoData,
  storeSakQuestionnaireData,
  removeSakPhotoData,
  removeSakQuestionnaireData
} from "../../helpers/client-state/sakLocalStorage";
import { triggerEvent, triggerEventOnce } from "../../helpers/observability/analytics";
import AlertMessage from "../Shared/AlertMessage";
import { Say } from "../../languages";
import { createTimeoutPromise } from "../../helpers/miscellaneous";
import { formatSakTopics } from "../../helpers/observability/analyticsUtils";

const ELETTER_TIMEOUT_MS = 45000;

const TourOrderConfirmation = () => {
  const { sakConfig, sponsoredChildData, orderData } = appSettings;
  const photoUploadEnabled = sakConfig?.photoUploadEnabled || false;
  const questionnaireEnabled = sakConfig?.questionnaireEnabled || false;
  const postOrderFlowEnabled = photoUploadEnabled || questionnaireEnabled;
  const {
    id: orderId = "",
    order_ref: orderRef,
    email,
    total,
    handshake,
    cssc,
    first_name: sponsorName
  } = (orderData || {}) as OrderData;
  const orderDataMissing = !orderData || !orderId;

  const isPostOrderFlowCompleted = photoUploadEnabled && hasPostOrderFlowCompletedId(orderId);
  const initialStatus = isPostOrderFlowCompleted ? "post-order-flow-completed" : "photo-pending";
  const [status, setStatus] = useState(initialStatus); // valid values: photo-pending, photo-loading, photo-review, photo-rejected, photo-skipped, post-order-flow-completed, questionnaire

  // Initialize uploadedPhotoData from local storage if available, which is needed if the page is reloaded (like on the confirmation page).
  const [uploadedPhotoData, setUploadedPhotoData] = useState(() => retrieveSakPhotoData());

  const [questionnaireData, setQuestionnaireData] = useState(() => retrieveSakQuestionnaireData());

  const { line_items } = (orderData || {}) as OrderData;
  const fallbackChildData = getFallbackChildData(line_items);
  const childName = sponsoredChildData?.givenName
    ? sponsoredChildData?.givenName
    : fallbackChildData?.givenName || "your sponsored child";
  const gender = sponsoredChildData?.gender || "";

  // Determine the objective pronoun for the child (her/him/them)
  const objectivePronoun =
    gender.toLowerCase() === "female"
      ? "her"
      : gender.toLowerCase() === "male"
      ? "him"
      : "them";

  const childPhotoUrl = fallbackChildData?.childMedia?.[0]?.cgpUrl || "";

  const texts = questionnaireEnabled
    ? {
        photoHeading: `Make a card for ${childName}`,
        photoParagraph: "Add a photo of yourself or your family and say hello!",
        photoReviewParagraph: `${childName} will love seeing you!`
      }
    : {
        photoHeading: "Add a Photo",
        photoParagraph: `Add a photo of yourself, or your family to share with ${childName}.`,
        photoReviewParagraph: `Share a photo of yourself or your family with ${childName}.`
      };

  const [eletterSending, setEletterSending] = useState(false);
  const [eletterError, setEletterError] = useState<string | null>(null);

  const clearEletterError = () => setEletterError(null);

  // Wrap the eletter request so that failures update the status.
  const sendEletter = async () => {
    clearEletterError();
    setEletterSending(true);

    // These params are used for both the generic eletter and the SAK eletter services.
    const requestParams = {
      childId: sponsoredChildData?.supportOfficeChildId,
      countryCode: sponsoredChildData?.countryCode,
      orderRef,
      orderId,
      cssc,
      orderHandshake: handshake,
      mediaUrl: uploadedPhotoData?.mediaUrl,
      mediaHandshake: uploadedPhotoData?.mediaHandshake,
      sponsorName,
      childName: sponsoredChildData?.givenName
    };

    // Get the SAK questionnaire and sponsor data from local storage if available.
    const parsedQuestionnaireData = retrieveSakQuestionnaireData();
    const parsedSponsorData = retrieveSakSponsorData();

    const topics =
      questionnaireEnabled && parsedQuestionnaireData
        ? formatSakTopics(parsedQuestionnaireData)
        : "";

    try {
      const eletterPromise =
        questionnaireEnabled && parsedQuestionnaireData && parsedSponsorData
          ? requestSakEletter({
              ...requestParams,
              sakQuestionnaireData: parsedQuestionnaireData,
              sakSponsorData: parsedSponsorData
            })
          : requestEletter(requestParams);

      await createTimeoutPromise(eletterPromise, ELETTER_TIMEOUT_MS);

      addPostOrderFlowCompletedId(orderId);
      questionnaireEnabled && triggerEventOnce("sak_message_send_success", { topics });
      setStatus("post-order-flow-completed");

      window.scrollTo(0, 0);
    } catch (err) {
      const errorMessage = questionnaireEnabled
        ? "There was a problem sending your message. Please try again."
        : "There was a problem sending your photo. Please try again.";

      setEletterError(errorMessage);

      console.error(errorMessage);

      if (questionnaireEnabled) {
        triggerEvent("sak_message_error", {
          error_message: errorMessage,
          error_type: "tour_post_checkout_error",
          topics
        });
      }

      wvusLogger.log({ message: errorMessage, error: err }, wvusLogger.getLogLevelNames().ERROR);

      triggerEvent("error_view", {
        error_message: errorMessage,
        error_type: "tour_order_error",
        event_label: "eletter_send",
        checkout_type: line_items?.length
          ? `flex_checkout:${line_items[0].product_sku}`
          : "flex_checkout"
      });
    } finally {
      setEletterSending(false);
    }
  };

  // When the photo step is confirmed, decide what to do next.
  const handlePhotoConfirmed = async (photoData) => {
    storeSakPhotoData(photoData);
    setUploadedPhotoData(photoData);

    window.scrollTo(0, 0);

    if (questionnaireEnabled) {
      // Move to the questionnaire step.
      setStatus("questionnaire");
    } else {
      // If no questionnaire, send the eletter immediately.
      await sendEletter();
    }
  };

  const handlePhotoReview = (data) => {
    const newPhotoData = {
      mediaUrl: data.imageUrl,
      mediaHandshake: data.imageHandshake
    };
    setUploadedPhotoData(newPhotoData);
  };

  const handlePhotoSkipped = () => {
    setStatus("photo-skipped");
  };

  const handleQuestionnaireSubmit = async (topic: string, response: string) => {
    storeSakQuestionnaireData({ topic, response });
    setQuestionnaireData({ topic, response });

    await sendEletter();
  };

  // Handler for tracking questionnaire view analytics
  const handleQuestionnaireView = (topic: string) => {
    triggerEvent("sak_message_compose_view", { topic });
  };

  // Handler for tracking when user starts interacting with the message box
  const handleMessageInteractionStart = (topic: string) => {
    triggerEvent("sak_message_compose_start", { topic });
  };

  // Clear sakPhotoData when status is set back to "photo-pending"
  useEffect(() => {
    if (orderDataMissing) {
      return;
    }

    if (status === "photo-pending") {
      removeSakPhotoData();
    } else if (status === "questionnaire") {
      removeSakQuestionnaireData();
      setQuestionnaireData(null);
    }
  }, [status, orderDataMissing]);

  useEffect(() => {
    if (!orderDataMissing) {
      return;
    }

    wvusLogger.log(
      {
        message: "Tour order confirmation rendered without order data",
        orderIdParam: getQueryParam("order_id") || null,
        hasOrderData: !!orderData
      },
      wvusLogger.getLogLevelNames().WARNING
    );
  }, [orderDataMissing, orderData]);

  if (orderDataMissing) {
    return (
      <div className="order-confirmation__wrapper">
        <AlertMessage alertType="info" visible={true}>
          <Say phrase="chosen.thank_you.order_details_unavailable" />
        </AlertMessage>
      </div>
    );
  }

  const sakConfirmationHeader = (
    <SAKConfirmationHeader
      visible={status === "photo-pending"}
      photoUrl={childPhotoUrl}
      childName={childName}
    />
  );

  const tourOrderConfirmCheck = (
    <TourOrderConfirmCheck
      visible={["photo-pending", "photo-skipped", "post-order-flow-completed"].includes(status)}
    />
  );

  const tourOrderDetails = (
    <TourOrderDetails
      visible={status === "photo-pending"}
      orderId={orderId}
      emailAddress={email}
      total={total}></TourOrderDetails>
  );

  const tourOrderConfirmationText =
    status !== "photo-pending" ? (
      <></>
    ) : questionnaireEnabled ? (
      <TourOrderConfirmationText
        className="sak"
        title="Congratulations!"
  message={`Sponsoring ${childName} is a beautiful way to help change lives — yours included! Why not send ${objectivePronoun} a quick card to say hello?`}
      />
    ) : photoUploadEnabled && status === "photo-pending" ? (
      <TourOrderConfirmationText
        title="Payment Confirmed!"
        message="Children LOVE to know more about their sponsor. Before you go, would you share a photo with your child?"
      />
    ) : (
      <TourOrderConfirmationText title="Payment Confirmed!" message="" />
    );

  const sakPreviewContainer = (
    <SAKPreviewContainer
      status={status}
      uploadedPhotoUrl={uploadedPhotoData?.mediaUrl}
      surveyCompleted={!!questionnaireData}
    />
  );

  const tourOrderPhotoUpload = (
    <TourOrderPhotoUpload
      visible={
        photoUploadEnabled &&
        status !== "photo-skipped" &&
        status !== "questionnaire" &&
        status !== "post-order-flow-completed"
      }
      photoModerationStep={status}
      setPhotoModerationStep={setStatus}
      onPhotoConfirmed={handlePhotoConfirmed}
      onPhotoSkipped={handlePhotoSkipped}
      onPhotoReview={handlePhotoReview}
      headingText={texts.photoHeading}
      paragraphText={texts.photoParagraph}
      reviewParagraphText={texts.photoReviewParagraph}
      showUploadedPhotoPreview={!questionnaireEnabled}
      eletterError={eletterError}
    />
  );

  const photoLoadingSpinner = status === "photo-loading" && (
    <>
      <div className="App AppLoad">
        <div className="spinner--wv-star">
          <div className="double-bounce1"></div>
          <div className="double-bounce2"></div>
        </div>
      </div>
    </>
  );

  const tourOrderQuestionnaire = (
    <TourOrderQuestionnaire
      visible={status === "questionnaire"}
      onSubmit={handleQuestionnaireSubmit}
      uploadedPhotoUrl={uploadedPhotoData?.mediaUrl || ""}
      onBackToPhoto={() => {
        clearEletterError();
        setStatus("photo-review");
      }}
      onSkip={async () => {
        await sendEletter();
      }}
      childName={childName}
      onQuestionnaireView={handleQuestionnaireView}
      onMessageInteractionStart={handleMessageInteractionStart}
      isSubmitting={eletterSending}
      error={eletterError}
      onClearEletterError={clearEletterError}
    />
  );

  const eletterSendingOverlay = eletterSending && (
    <div className="overlay">
      <div className="overlay-content">
        <div className="spinner--wv-star">
          <div className="double-bounce1"></div>
          <div className="double-bounce2"></div>
        </div>
      </div>
    </div>
  );

  const tourOrderComplete = (
    <TourOrderConfirmComplete
      visible={
        (postOrderFlowEnabled && ["photo-skipped", "post-order-flow-completed"].includes(status)) ||
        (!postOrderFlowEnabled && status === "photo-pending")
      }
      childDetails={sponsoredChildData}
      status={status}
    />
  );

  const components = questionnaireEnabled ? (
    <>
      {sakConfirmationHeader}
      {tourOrderDetails}
      {tourOrderConfirmationText}
      {sakPreviewContainer}
      {tourOrderPhotoUpload}
      {photoLoadingSpinner}
      {tourOrderQuestionnaire}
      {eletterSendingOverlay}
      {tourOrderComplete}
    </>
  ) : (
    <>
      {tourOrderConfirmCheck}
      {tourOrderConfirmationText}
      {tourOrderDetails}
      {tourOrderPhotoUpload}
      {photoLoadingSpinner}
      {tourOrderComplete}
    </>
  );

  return <div className="order-confirmation__wrapper">{components}</div>;
};

export default TourOrderConfirmation;
